/**
 * admin/overlay.tsx — the shared teal modal shell + small presentational helpers every admin
 * governance panel is built from. This is the ONE place the overlay contract lives, so the six
 * panels (Appointments, ToolGovernance, AccessRequirements, PlatformSettings, Headcount, TermsOfUse)
 * all open identically and match the "Upload a tool" overlay pixel-for-pixel.
 *
 * WHY A WRAPPER, not each panel calling OverlayFrame itself. The upload journey drives OverlayFrame
 * from `useUploadOverlay` (URL-derived step state, its own dialogRef + Tab/Escape handler). Admin
 * panels have none of that — they are one static screen apiece, opened in place over AdminHome by a
 * boolean, exactly as the Profile "Publish" button opens ShareOverlay (Profile.tsx / ShareOverlay.tsx).
 * So AdminOverlay is the admin-side analogue of that host contract: a local dialogRef, the sanctioned
 * `useFocusTrap` (a11y.ts) for Tab-cycle + Escape + RETURN-FOCUS (which FlowPickerReskin's hand-rolled
 * onKeyDown does NOT do), and the same OverlayFrame + ModalShell the upload overlay uses so the frame,
 * scrim, entrance motion and dimensions are shared, not re-implemented.
 *
 * FOCUS TRAP — one mechanism, not two. OverlayFrame focuses its dialog box on mount (its ref callback)
 * and its `onKeyDown` prop is meant to cycle Tab. We instead pass a NO-OP `onKeyDown` and let
 * `useFocusTrap` own Tab + Escape from a single capture-phase document listener — layering the hook's
 * listener on top of an OverlayFrame that ALSO trapped Tab would double-handle the key. The hook adds
 * return-focus on unmount, which is the whole reason to prefer it here. The dialog box already carries
 * `tabIndex={-1}` inside OverlayFrame, which is the element the hook falls back to focusing.
 */
import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { AnimatePresence } from 'motion/react';
import { OverlayFrame } from '../tools/upload/design/OverlayFrame';
import { ModalShell } from '../tools/upload/design/ModalShell';
import { useFocusTrap } from '../a11y';

export interface AdminOverlayProps {
  /** The dialog's visible title (P1-style header, wired as the accessible name via aria-labelledby). */
  title: string;
  /** Dismiss — Escape, backdrop press, and the header close button all call this. */
  onClose: () => void;
  /** The panel body. Scrolls inside the fixed-height modal when it overflows. */
  children: ReactNode;
  /** Optional footer region (actions), pinned below the scrolling body with the design's top rule. */
  footer?: ReactNode;
}

/**
 * The teal admin modal. Renders the upload overlay's exact frame with a titled header, a scrolling
 * body, and an optional footer. Opening it is the caller's job (conditional mount); this owns only the
 * in-dialog behaviour.
 */
export function AdminOverlay({ title, onClose, children, footer }: AdminOverlayProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  // useFocusTrap owns Tab + Escape (capture phase) + return-focus. So OverlayFrame's own onKeyDown is
  // a deliberate no-op — see the header. The hook needs a stable onClose; the caller passes one.
  useFocusTrap(dialogRef, onClose);
  const noopKeyDown = (_event: KeyboardEvent<HTMLDivElement>) => {};

  // One stable id per mount, for the <h2> the frame points aria-labelledby at. useId is React's
  // purpose-built API for this — SSR-safe and pure, unlike incrementing a module counter in render.
  const titleId = useId();

  return (
    <OverlayFrame dialogRef={dialogRef} onKeyDown={noopKeyDown} onClose={onClose}>
      <AnimatePresence mode="wait" initial={false}>
        <ModalShell
          key="admin-panel"
          labelledBy={titleId}
          headerInset="title"
          bodyScroll
          header={
            <div className="flex flex-wrap items-center justify-between gap-7">
              <h2 id={titleId} className="min-w-0 text-2xl font-light text-text">
                {title}
              </h2>
              {/* The design draws no close affordance on the upload frame (Escape / click-off only),
                  but an admin panel opened FROM a grid tile reads better with an explicit close, and
                  it is a real focusable control the trap can land on. Plain button, teal hover. */}
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="shrink-0 rounded-md px-3 py-1 text-base font-light text-text-inactive outline-none transition-colors duration-micro ease-in-out hover:bg-accent-subtle hover:text-text focus-visible:border focus-visible:border-ring-focus"
              >
                Close
              </button>
            </div>
          }
          {...(footer ? { footer: <div className="border-t border-ink">{footer}</div> } : {})}
        >
          {/* `admin-surface` scopes the darker --text-inactive (design-system.css) to the
              admin panels' muted text, without touching the global token. */}
          <div className="admin-surface pt-8 pb-11">{children}</div>
        </ModalShell>
      </AnimatePresence>
    </OverlayFrame>
  );
}

/* ── Small teal helpers, so the six panels agree on the recurring bits ───────────────────────────── */

/** The always-present provisional-status note, restyled teal. Small, muted, never shouty. */
export function ProvisionalNote() {
  return (
    <p role="note" className="mb-7 text-sm font-light text-text-inactive">
      <span className="font-medium text-text">Provisional interface.</span> A working engineering
      build for an appointed administrator. Its layout and wording are not the final design.
    </p>
  );
}

/** A section heading inside a panel — the 1px ink rule + label motif from the upload overlay. */
export function SectionHeader({ children }: { children: ReactNode }) {
  return (
    <div className="mt-9 mb-5 first:mt-0">
      <h3 className="text-base font-medium text-text">{children}</h3>
      <div className="mt-2 h-px bg-ink/20" />
    </div>
  );
}

/** An inline error line (mutation failure), teal-error styled, role=alert for AT. */
export function ErrorLine({ children, id }: { children: ReactNode; id?: string }) {
  if (!children) return null;
  return (
    <p
      {...(id ? { id } : {})}
      role="alert"
      className="mt-3 rounded-md border border-error px-field-x py-2 text-sm font-light text-error"
    >
      {children}
    </p>
  );
}

/** A neutral notice / helper paragraph — muted, constrained width for readability. */
export function Notice({ children }: { children: ReactNode }) {
  return <p className="max-w-[60ch] text-sm font-light text-text-inactive">{children}</p>;
}

/** The empty-state line ("nobody currently holds an appointment"). */
export function EmptyState({ children }: { children: ReactNode }) {
  return <p className="my-6 text-sm font-light italic text-text-inactive">{children}</p>;
}

/**
 * The teal "you don't hold this appointment" state, for a panel/route reached without its role. The
 * navy sibling in shared.tsx stays for the out-of-scope queue pages; this is the design-system one.
 */
export function NotPermitted({ what }: { what: string }) {
  return (
    <section aria-labelledby="not-permitted-heading" className="font-sans">
      <h1 id="not-permitted-heading" className="text-2xl font-light text-text">
        {what}
      </h1>
      <p role="alert" className="mt-5 max-w-[60ch] text-base font-light text-text-inactive">
        You don&rsquo;t currently hold the appointment this screen needs. If you believe you should,
        ask the platform owner to check the administration surface.
      </p>
    </section>
  );
}

/** A small teal spinner for the fetch-on-open state. Pure CSS ring; respects reduced-motion via CSS. */
export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-5 py-13" role="status">
      <span
        aria-hidden="true"
        className="h-5 w-5 animate-spin rounded-full border-2 border-accent-subtle border-t-accent"
      />
      <span className="text-sm font-light text-text-inactive">{label}</span>
    </div>
  );
}

/**
 * fetch-on-open for a panel. Runs `load` once when the overlay mounts (and the panel is open), tracks
 * loading / error / data, and drops a reply that lands after unmount. Panels use this to hydrate their
 * own data with a Spinner while it resolves — the confirmed model (matches ShareOverlay.loadFlows),
 * so AdminHome never has to preload anything. `reload` re-runs it after a mutation when a panel wants
 * to refresh in place rather than close.
 *
 * A `load` that resolves to a discriminated result (e.g. {state:'forbidden'}) is passed straight
 * through as `data`; this hook only owns the request lifecycle, never the panel's own state shape.
 */
export function useOverlayData<T>(load: () => Promise<T>): {
  data: T | null;
  loading: boolean;
  error: boolean;
  reload: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // `load` identity is not depended on (callers pass an inline closure); a ref keeps the latest
  // without re-firing the effect, matching how ShareOverlay keys its open-effect on `open` alone.
  const loadRef = useRef(load);
  loadRef.current = load;
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    loadRef
      .current()
      .then((result) => {
        if (!alive) return;
        setData(result);
      })
      .catch(() => {
        if (!alive) return;
        setError(true);
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [nonce]);

  return { data, loading, error, reload: () => setNonce((n) => n + 1) };
}

/** Re-export the dialog ref type for panels that need to type a returnFocusRef contract. */
export type ReturnFocusRef = MutableRefObject<HTMLElement | null>;
